import {
    AutomationEntityRequest,
    ChecklistItemTemplateEntityRequest,
    CommonSagaActionsData,
    StepConfigEntityRequest,
    StepRunChecklistItemEntityRequest,
    StepRunEntityRequest,
    WorkflowEntityRequest,
    OrgEntityRequest,
    WorkflowRunEntityRequest,
} from '@/actions/entities';
import {
    AddIndividualTesterToBuildRequest,
    AssignBetaGroupsToBuildRequest,
    CreateChecklistItemRequest,
    CreateOrUpdateBuildLocalizationsRequest,
    DeleteChecklistItemRequest,
    EndBetaSoakRequest,
    PromoteBuildsToTestingTrackRequest,
    UpdateSelectedBuildsInAppStoreRequest,
    StepRunUpdatable,
    SubmitBetaBuildToReviewRequest,
    UpdateChecklistItemCommentRequest,
    UpdateChecklistItemRequest,
    UpdateChecklistItemStatusRequest,
    UpdateReviewDetailsRequest,
    SubmitAppForReviewRequest,
    TagReleaseRequest,
    UpdateAdditionalSelectedBuildsInAppStoreRequest,
    UpdateAppStoreSettingsRequest,
    UpdateMetadataRequest,
    ApproveMetadataRequest,
    ReleaseAppleVersionRequest,
    UpdateScreenshotsApprovalRequest,
    UpdateApprovalOverrideRequest,
    DeleteScreenshotRequest,
    AddReviewSubmissionItemRequest,
    RemoveReviewSubmissionItemRequest,
    RetryArtifactUploadRequest,
    TriggerBuildSystemWorkflowRequest,
    UpdateRegressionStatusRequest,
    UpdateAndroidPhasedReleaseRequest,
    UpdateApplePhasedReleaseRequest,
    UpdateHuaweiPhasedReleaseRequest,
    UpdateOrgChecklistItemTemplateRequest,
    DeleteOrgChecklistItemTemplateRequest,
    LockBuildRequest,
    OverrideCIFailureRequest,
    CreateOrgChecklistItemTemplateRequest,
} from '@/actions/stepRun';
import { OrgUpdatable } from '@/actions/organization';
import {
    CreateChecklistItemTemplateRequest,
    DeleteChecklistItemTemplateRequest,
    StepConfigUpdatable,
    UpdateAutomationSettingsRequest,
    UpdateBetaTestingStyleRequest,
    UpdateChecklistItemTemplateOrderRequest,
    UpdateChecklistItemTemplateRequest,
    UpdateRegressionTestingStrategyRequest,
    UpdateStepConfigRequest,
} from '@/actions/stepConfig';
import { WorkflowUpdatable } from '@/actions/workflow';
import { allCases } from '@/typescriptHelpers';
import { DestinationUpdatable } from '@/actions/updatable';
import { UpdateDestinationDetailsRequest } from '@/types/destinations';
import {
    TriggerBatchActionRequest,
    WorkflowRunUpdatable,
    PingPendingChecklistItemsForStepGroupRequest,
    BulkMarkCLIsAsCompletedRequest,
    UpdateDestinationStatusesRequest,
} from './workflowRun';

export type RunwayUpdatable =
    | StepRunUpdatable
    | StepConfigUpdatable
    | WorkflowRunUpdatable
    | WorkflowUpdatable
    | OrgUpdatable
    | DestinationUpdatable;

export const AllRunwayUpdatables = allCases({
    ...StepRunUpdatable,
    ...StepConfigUpdatable,
    ...WorkflowRunUpdatable,
    ...WorkflowUpdatable,
    ...OrgUpdatable,
    ...DestinationUpdatable,
});

export interface DestinationEntityRequest extends OrgEntityRequest {
    appId: string;
    destinationId: string;
}

// More specific types should go first so they are caught first
export type UpdatableRequestDataType<T extends RunwayUpdatable> =
    // Step Run updatables
    T extends StepRunUpdatable.EndBetaSoakPeriod ? EndBetaSoakRequest
    : T extends StepRunUpdatable.AssignBetaGroupsToBuild ? AssignBetaGroupsToBuildRequest
    : T extends StepRunUpdatable.CreateOrUpdateLocalizationNotesForBuild ? CreateOrUpdateBuildLocalizationsRequest
    : T extends StepRunUpdatable.SubmitBetaBuildToReview ? SubmitBetaBuildToReviewRequest
    : T extends StepRunUpdatable.AddIndividualTesterToBuild ? AddIndividualTesterToBuildRequest
    : T extends StepRunUpdatable.PromoteBuildsToTestingTrack ? PromoteBuildsToTestingTrackRequest
    : T extends StepRunUpdatable.UpdateSelectedBuildsInAppStore ? UpdateSelectedBuildsInAppStoreRequest
    : T extends StepRunUpdatable.SetAdditionalSelectedBuilds ? UpdateAdditionalSelectedBuildsInAppStoreRequest
    : T extends StepRunUpdatable.SubmitAppForReview ? SubmitAppForReviewRequest
    : T extends StepRunUpdatable.UpdateAppStoreSettings ? UpdateAppStoreSettingsRequest
    : T extends StepRunUpdatable.UpdateReviewDetails ? UpdateReviewDetailsRequest
    : T extends StepRunUpdatable.RejectAppleSubmission ? void
    : T extends StepRunUpdatable.ReleaseAppleVersion ? ReleaseAppleVersionRequest
    : T extends StepRunUpdatable.MarkAndroidSubmissionRejected ? void
    : T extends StepRunUpdatable.UpdateApplePhasedRelease ? UpdateApplePhasedReleaseRequest
    : T extends StepRunUpdatable.UpdateGooglePhasedRelease ? UpdateAndroidPhasedReleaseRequest
    : T extends StepRunUpdatable.UpdateHuaweiPhasedRelease ? UpdateHuaweiPhasedReleaseRequest
    : T extends StepRunUpdatable.PauseGooglePhasedRelease ? UpdateAndroidPhasedReleaseRequest
    : T extends StepRunUpdatable.PauseHuaweiPhasedRelease ? UpdateHuaweiPhasedReleaseRequest
    : T extends StepRunUpdatable.ResumeApplePhasedRelease ? UpdateApplePhasedReleaseRequest
    : T extends StepRunUpdatable.CompleteGooglePhasedRelease ? UpdateAndroidPhasedReleaseRequest
    : T extends StepRunUpdatable.PauseApplePhasedRelease ? UpdateApplePhasedReleaseRequest
    : T extends StepRunUpdatable.CompleteApplePhasedRelease ? UpdateApplePhasedReleaseRequest
    : T extends StepRunUpdatable.CompleteHuaweiPhasedRelease ? UpdateHuaweiPhasedReleaseRequest
    : T extends StepRunUpdatable.StartAutomatedAndroidStagedRollout ? void
    : T extends StepRunUpdatable.TagRelease ? TagReleaseRequest
    : T extends StepRunUpdatable.RejectVersion ? void
    : T extends StepRunUpdatable.CreateStepRunChecklistItem ? CreateChecklistItemRequest
    : T extends StepRunUpdatable.UpdateStepRunChecklistItem ? UpdateChecklistItemRequest
    : T extends StepRunUpdatable.DeleteStepRunChecklistItem ? DeleteChecklistItemRequest
    : T extends StepRunUpdatable.UpdateChecklistItemComment ? UpdateChecklistItemCommentRequest
    : T extends StepRunUpdatable.UpdateChecklistItemStatus ? UpdateChecklistItemStatusRequest
    : T extends StepRunUpdatable.UpdateMetadata ? UpdateMetadataRequest
    : T extends StepRunUpdatable.ApproveMetadata ? ApproveMetadataRequest
    : T extends StepRunUpdatable.DeleteScreenshot ? DeleteScreenshotRequest
    : T extends StepRunUpdatable.UpdateScreenshotsApproval ? UpdateScreenshotsApprovalRequest
    : T extends StepRunUpdatable.UpdateApprovalOverride ? UpdateApprovalOverrideRequest
    : T extends StepRunUpdatable.AddReviewSubmissionItem ? AddReviewSubmissionItemRequest
    : T extends StepRunUpdatable.RemoveReviewSubmissionItem ? RemoveReviewSubmissionItemRequest
    : T extends StepRunUpdatable.UnblockReviewSubmission ? void
    : T extends StepRunUpdatable.CancelReviewSubmission ? void
    : T extends StepRunUpdatable.TriggerBuildSystemWorkflow ? TriggerBuildSystemWorkflowRequest
    : T extends StepRunUpdatable.RetryArtifactUpload ? RetryArtifactUploadRequest
    : T extends StepRunUpdatable.LockBuild ? LockBuildRequest
    : T extends StepRunUpdatable.UnlockBuild ? void
    : T extends StepRunUpdatable.OverrideCIFailure ? OverrideCIFailureRequest
    : T extends StepRunUpdatable.UpdateRegressionStatus ? UpdateRegressionStatusRequest
    : T extends StepRunUpdatable.StartResignSequence ? void
    : // StepConfig updatables
    T extends StepConfigUpdatable.UpdateBetaTestingStyle ? UpdateBetaTestingStyleRequest
    : T extends StepConfigUpdatable.CreateChecklistItemTemplate ? CreateChecklistItemTemplateRequest
    : T extends StepConfigUpdatable.UpdateChecklistItemTemplate ? UpdateChecklistItemTemplateRequest
    : T extends StepConfigUpdatable.DeleteChecklistItemTemplate ? DeleteChecklistItemTemplateRequest
    : T extends StepConfigUpdatable.UpdateChecklistItemTemplateOrder ? UpdateChecklistItemTemplateOrderRequest
    : T extends StepConfigUpdatable.UpdateStepConfig ? UpdateStepConfigRequest
    : T extends StepConfigUpdatable.UpdateRegressionTestingStrategy ? UpdateRegressionTestingStrategyRequest
    : // Workflow updatables
    T extends WorkflowUpdatable.UpdateAutomationSettings ? UpdateAutomationSettingsRequest
    : T extends WorkflowUpdatable ? WorkflowEntityRequest
    : // WorkflowRun updatables
    T extends WorkflowRunUpdatable.TriggerBatchAction ? TriggerBatchActionRequest
    : T extends WorkflowRunUpdatable.PingPendingChecklistItemsForStepGroup ?
        PingPendingChecklistItemsForStepGroupRequest
    : T extends WorkflowRunUpdatable.BulkMarkCLIsAsCompleted ? BulkMarkCLIsAsCompletedRequest
    : T extends WorkflowRunUpdatable.UpdateDestinationStatuses ? UpdateDestinationStatusesRequest
    : T extends WorkflowRunUpdatable ? WorkflowRunEntityRequest
    : // Org updatables
    T extends OrgUpdatable.CreateOrgChecklistItemTemplate ? CreateOrgChecklistItemTemplateRequest
    : T extends OrgUpdatable.UpdateOrgChecklistItemTemplate ? UpdateOrgChecklistItemTemplateRequest
    : T extends OrgUpdatable.DeleteOrgChecklistItemTemplate ? DeleteOrgChecklistItemTemplateRequest
    : // Destination updatables
    T extends DestinationUpdatable.UpdateDestinationDetails ? UpdateDestinationDetailsRequest
    : void;

export type UpdatableResourceIdsType<T extends RunwayUpdatable> = // Step Run updatables
    T extends (
        | StepRunUpdatable.UpdateStepRunChecklistItem
        | StepRunUpdatable.DeleteStepRunChecklistItem
        | StepRunUpdatable.UpdateChecklistItemComment
        | StepRunUpdatable.UpdateChecklistItemStatus
        | StepRunUpdatable.PingChecklistItem
    ) ?
        StepRunChecklistItemEntityRequest
    : T extends StepRunUpdatable ? StepRunEntityRequest
    : // StepConfig updatables
    T extends StepConfigUpdatable.UpdateChecklistItemTemplate | StepConfigUpdatable.DeleteChecklistItemTemplate ?
        ChecklistItemTemplateEntityRequest
    : T extends StepConfigUpdatable ? StepConfigEntityRequest
    : // Workflow updatables
    T extends WorkflowUpdatable.UpdateAutomationSettings ? AutomationEntityRequest
    : T extends WorkflowUpdatable ? WorkflowEntityRequest
    : // WorkflowRun updatables
    T extends WorkflowRunUpdatable.TriggerBatchAction ? WorkflowRunEntityRequest
    : T extends WorkflowRunUpdatable.PingPendingChecklistItemsForStepGroup ? WorkflowRunEntityRequest
    : T extends WorkflowRunUpdatable.BulkMarkCLIsAsCompleted ? WorkflowRunEntityRequest
    : T extends WorkflowRunUpdatable.UpdateDestinationStatuses ? WorkflowRunEntityRequest
    : // Org updatables
    T extends OrgUpdatable.CreateOrgChecklistItemTemplate ? OrgEntityRequest
    : T extends OrgUpdatable.UpdateOrgChecklistItemTemplate ? OrgEntityRequest
    : T extends OrgUpdatable.DeleteOrgChecklistItemTemplate ? OrgEntityRequest
    : // Destination updatables
    T extends DestinationUpdatable.UpdateDestinationDetails ? DestinationEntityRequest
    : never;

export interface UpdatableRequest<T extends RunwayUpdatable> {
    requestData: UpdatableRequestDataType<T>;
    resourceIds: UpdatableResourceIdsType<T>;
    id: string;
}

export interface TryUpdatableAction<T extends RunwayUpdatable = RunwayUpdatable> extends CommonSagaActionsData {
    type: T;
    payload: UpdatableRequest<T>;
}

export const tryUpdatable: <T extends RunwayUpdatable>(
    type: T,
    resourceIds: UpdatableResourceIdsType<T>,
    requestData: UpdatableRequestDataType<T>,
    id: string,
) => TryUpdatableAction<T> = (type, resourceIds, requestData, id) => {
    return {
        type,
        payload: {
            resourceIds,
            requestData,
            id,
        },
    };
};

// Returns true if the same action can be called multiple times for different ids
// In practice, if the action supports parallelism, we'll use takeEvery instead of takeLatest
export const hasSupportForParallelism = (updatable: RunwayUpdatable) => {
    switch (updatable) {
        case StepRunUpdatable.DeleteScreenshot:
            return true;
        default:
            return false;
    }
};
